
import { useEffect, useState } from 'react';
import { DebugRequestService } from '@/services/interview/requests/debugRequestService';
import { InterviewRequestResponse } from '@/services/interview/requests/types';

interface UseDebugInterviewRequestProps {
  requestLink: string;
  isPageLoading?: boolean;
}

interface UseDebugInterviewRequestReturn {
  loading: boolean;
  error: string | null;
  interviewRequest: InterviewRequestResponse | null;
  debugInfo: any;
  networkInfo: any;
}

export const useDebugInterviewRequest = ({ 
  requestLink, 
  isPageLoading = false 
}: UseDebugInterviewRequestProps): UseDebugInterviewRequestReturn => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [interviewRequest, setInterviewRequest] = useState<InterviewRequestResponse | null>(null);
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const [networkInfo, setNetworkInfo] = useState<any>({});

  useEffect(() => {
    if (isPageLoading || !requestLink) {
      console.log('🔄 DEBUG HOOK: Waiting - isPageLoading:', isPageLoading, 'requestLink:', requestLink);
      return;
    }

    const fetchRequest = async () => {
      try {
        console.log('🔄 DEBUG HOOK: Starting comprehensive debug fetch for requestLink:', requestLink);
        
        // Capture network and browser info
        const networkInfo = {
          userAgent: navigator.userAgent,
          language: navigator.language,
          cookiesEnabled: navigator.cookieEnabled,
          onLine: navigator.onLine,
          url: window.location.href,
          referrer: document.referrer,
          timestamp: new Date().toISOString(),
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          // Check for any cached data
          localStorage: {
            hasSupabaseData: !!localStorage.getItem('supabase.auth.token'),
            keys: Object.keys(localStorage)
          },
          sessionStorage: {
            keys: Object.keys(sessionStorage)
          }
        };

        setNetworkInfo(networkInfo);
        console.log('🌐 DEBUG HOOK: Network/Browser info:', networkInfo);
        
        setLoading(true);
        setError(null);
        
        const startTime = performance.now();
        const { data, error: fetchError, debugInfo } = await DebugRequestService.getRequestByLinkWithFullDebug(requestLink);
        const endTime = performance.now();
        
        const finalDebugInfo = {
          ...debugInfo,
          totalTime: `${endTime - startTime}ms`,
          networkInfo: networkInfo
        };
        
        setDebugInfo(finalDebugInfo);
        
        if (fetchError) {
          console.error('❌ DEBUG HOOK: Service returned error:', fetchError);
          console.error('🔍 DEBUG HOOK: Full debug info:', finalDebugInfo);
          setError(fetchError);
          setInterviewRequest(null);
        } else if (data) {
          console.log('✅ DEBUG HOOK: Service returned data');
          console.log('🔍 DEBUG HOOK: Full debug analysis:', finalDebugInfo);
          setInterviewRequest(data);
          setError(null);
        } else {
          console.error('❌ DEBUG HOOK: Service returned no data and no error');
          console.error('🔍 DEBUG HOOK: Full debug info:', finalDebugInfo);
          setError('No data received');
          setInterviewRequest(null);
        }
      } catch (err) {
        console.error('💥 DEBUG HOOK: Exception during fetch:', err);
        setError('Failed to load interview request');
        setInterviewRequest(null);
      } finally {
        setLoading(false);
        console.log('🏁 DEBUG HOOK: Debug fetch completed');
      }
    };

    fetchRequest();
  }, [requestLink, isPageLoading]);

  // Final state debug
  console.log('🎯 DEBUG HOOK: Final state:', {
    loading,
    hasError: !!error,
    errorMessage: error,
    hasInterviewRequest: !!interviewRequest,
    interviewRequestQuestion: interviewRequest?.question || 'NO_QUESTION_IN_STATE',
    debugSteps: debugInfo?.steps?.length || 0
  });

  return { loading, error, interviewRequest, debugInfo, networkInfo };
};
